// useClientView — fetches the configured client registry and the per-client
// payload (optionally narrowed to a specific yearWeek). Mirrors the shape of
// useMarketTracks: the React layer only consumes derived state.

import { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const useClientView = ({ clientId, yearWeek } = {}) => {
    const [clients, setClients] = useState([]);
    const [defaultClientId, setDefaultClientId] = useState(null);
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Load registry once
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await axios.get(`${API}/clients`);
                if (cancelled) return;
                const list = Array.isArray(r.data?.clients) ? r.data.clients : [];
                setClients(list);
                setDefaultClientId(r.data?.default || (list[0]?.id ?? null));
            } catch (e) {
                if (!cancelled) setError(e?.response?.data?.detail || e.message || "Failed to load clients");
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Load the per-client payload when the route changes
    useEffect(() => {
        if (!clientId) {
            setPayload(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        (async () => {
            try {
                const url = yearWeek
                    ? `${API}/clients/${encodeURIComponent(clientId)}/${encodeURIComponent(yearWeek)}`
                    : `${API}/clients/${encodeURIComponent(clientId)}`;
                const r = await axios.get(url);
                if (!cancelled) setPayload(r.data);
            } catch (e) {
                if (!cancelled) {
                    const detail = e?.response?.data?.detail;
                    if (detail && typeof detail === "object") {
                        setError(detail);
                    } else {
                        setError({ message: detail || e?.message || "Failed to load client view", status: e?.response?.status });
                    }
                    setPayload(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [clientId, yearWeek]);

    const derived = useMemo(() => {
        if (!payload) {
            return {
                client: null,
                clientProfile: null,
                weeklyLens: null,
                trackCards: [],
                impactCellAssessments: [],
                organizationalExposureMatrix: null,
                actionBacklog: [],
                history: null,
                quality: null,
                availableYearWeeks: [],
                latestYearWeek: null,
                schemaVersion: null,
                generatedAt: null,
            };
        }
        return {
            client: payload.client || null,
            clientProfile: payload.clientProfile || null,
            weeklyLens: payload.weeklyLens || null,
            trackCards: Array.isArray(payload.trackCards) ? payload.trackCards : [],
            // `impactCellAssessments` is the canonical flat source per
            // `client-signal-view-v2.1-matrix-cells`. The legacy `impactMatrix`
            // alias is intentionally NOT read here so the n8n pipeline can
            // drop the duplicate field from the payload.
            impactCellAssessments: Array.isArray(payload.impactCellAssessments) ? payload.impactCellAssessments : [],
            organizationalExposureMatrix: Array.isArray(payload.organizationalExposureMatrix) ? payload.organizationalExposureMatrix : null,
            actionBacklog: Array.isArray(payload.actionBacklog) ? payload.actionBacklog : [],
            history: payload.history || null,
            quality: payload.quality || null,
            availableYearWeeks: Array.isArray(payload.availableYearWeeks) ? payload.availableYearWeeks : [],
            latestYearWeek: payload.latestYearWeek || null,
            schemaVersion: payload.schemaVersion || null,
            generatedAt: payload.generatedAt || null,
            selectedYearWeek: payload.selectedYearWeek || null,
        };
    }, [payload]);

    return {
        clients,
        defaultClientId,
        loading,
        error,
        ...derived,
    };
};

export default useClientView;
